import type { State as CompanyState } from '@/pages/CompanyReport/redux/interface';
import type { LocationState } from 'redux-first-router';
import { toCurrencyTruncated, toTruncated } from '@/utilities/formatting';
import { pathConstructor } from '@/hooks/useCompany/utils';

const formatter = (
  value?: number | null,
  currencySymbol?: string,
  significance = 2
) =>
  value === null || value === undefined
    ? 'n/a'
    : `${currencySymbol}${toTruncated(value, significance)}`;

const formatterFull = (
  value?: number | null,
  iso?: string,
  significance = 2
) =>
  value === null || value === undefined
    ? 'n/a'
    : toCurrencyTruncated(value, significance, iso);

interface ReduxState {
  company: CompanyState;
  location: LocationState;
}
export function selector(state: ReduxState) {
  try {
    const path = pathConstructor(state.location);
    const companyId = state.company.urlToIdMapping[path];
    const analysis = state.company.analysis[companyId];
    const summary = state.company.summary[companyId];
    const { reportingCurrencySymbol, reportingCurrencyISO } =
      state.company.formatting[companyId];
    const {
      pastRevenue,
      pastRevenueUnit,
      pastCostOfRevenue,
      pastCostOfRevenueUnit,
      pastGrossProfit,
      pastGrossProfitUnit,
      pastEarnings,
      pastEarningsUnit,
    } = analysis;

    const revenue = pastRevenue * pastRevenueUnit;
    const costOfRevenue = pastCostOfRevenue * pastCostOfRevenueUnit;
    const grossProfit = pastGrossProfit * pastGrossProfitUnit;
    const expenses =
      pastGrossProfit * pastGrossProfitUnit - pastEarnings * pastEarningsUnit;
    const earnings = pastEarnings * pastEarningsUnit;
    return {
      tickerSymbol: summary.tickerSymbol,
      revenue,
      costOfRevenue,
      grossProfit,
      expenses,
      earnings,
      revenueFormatted: formatter(revenue, reportingCurrencySymbol),
      costOfRevenueFormatted: formatter(costOfRevenue, reportingCurrencySymbol),
      grossProfitFormatted: formatter(grossProfit, reportingCurrencySymbol, 1),
      expensesFormatted: formatter(expenses, reportingCurrencySymbol, 1),
      earningsFormatted: formatter(earnings, reportingCurrencySymbol, 1),
      revenueFormattedFull: formatterFull(revenue, reportingCurrencyISO),
      costOfRevenueFormattedFull: formatterFull(
        costOfRevenue,
        reportingCurrencyISO
      ),
      grossProfitFormattedFull: formatterFull(
        grossProfit,
        reportingCurrencyISO
      ),
      expensesFormattedFull: formatterFull(expenses, reportingCurrencyISO),
      earningsFormattedFull: formatterFull(earnings, reportingCurrencyISO),
    };
  } catch (e) {
    return undefined;
  }
}
